// --- React & Redux
import React, { useEffect, useRef, useState } from "react";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- External UI Libraries
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Input from "@dtci/spellbook/Input";
import Select from "@dtci/spellbook/Select";
// --- Components
import { InputLabel } from "tap/components/InputLabel";
// --- Actions & Hooks
import { usePMXDeliveriesData } from "../../hooks/usePMXDeliveriesData";
import { usePmxDeliveriesActions } from "tap/store/pmxDeliveries/usePmxDeliveries.js";
// --- Utilities
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import {
  buildFilterFromParams,
  cleanPMXFilter,
} from "./utils/pmxDeliveriesSearchUtils";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
// --- Styles
import "./styles.scss";

export function PMXDeliveriesSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const {
    fetchPMXDeliveries,
    updatePMXDeliveries,
    clearPMXDeliveries,
    pmxDeliveriesRange,
    pmxDeliveriesSort,
    itemsPerPage,
  } = usePMXDeliveriesData();

  const { updatePMXDeliveriesSearchForm } = usePmxDeliveriesActions();

  const { filterForm, actionOptions, statusOptions } = useSelector(
    (s) => s.pmxDeliveries,
  );

  const [validationErrors, setValidationErrors] = useState({
    skuId: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Fetch data on every navigation to this page
  useEffect(() => {
    const params = parseSearchParams(location.search);

    // Build filter data from URL params using helper
    const filterData = buildFilterFromParams(params, {
      actionOptions,
      statusOptions,
    });

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updatePMXDeliveries({ itemsPerPage: filterData.pageSize });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    // Update form state from URL params
    if (hasUrlParams) {
      updatePMXDeliveriesSearchForm({ key: "filterForm", data: filterData });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Fetch data helper
  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchPMXDeliveries(range, filterData);

    if (data) {
      updatePMXDeliveries({
        deliveries: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber ?? 0,
        pmxDeliveriesRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset filters - only navigate, let useEffect handle the fetch
  const handleResetFilter = () => {
    navigate(location.pathname);
    clearPMXDeliveries();
    setValidationErrors({ skuId: undefined });
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (["skuId"].includes(field)) {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updatePMXDeliveriesSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    // Debounced search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanPMXFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          pmxDeliveriesSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  return (
    <Row className="deliveries-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Sku">
          <Input
            value={filterForm?.skuId || ""}
            onChange={(e) => handleInputChange("skuId", e.target.value)}
            width="auto"
            id="skuId"
            name="skuId"
            placeholder="Enter"
            className={validationErrors.skuId ? "input-error" : ""}
          />
          {validationErrors.skuId && (
            <div className="validation-error">{validationErrors.skuId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Action">
          <Select
            options={actionOptions}
            isClearable
            isSearchable
            onChange={(value) => handleInputChange("action", value)}
            value={filterForm?.action || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Status">
          <Select
            options={statusOptions}
            isClearable
            isSearchable
            onChange={(value) => handleInputChange("status", value)}
            value={filterForm?.status || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
